// src/pages/credit/CreditNoteJsonUpload.jsx
import React, { useMemo, useState } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import API_BASE_URL from "../../utils/config";
import { FiUser, FiCheckCircle, FiXCircle } from "react-icons/fi";

export default function CreditNoteJsonUpload() {
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState([]); // [{file, name, preview, local, error, customer, customerAuto}]
  const [loading, setLoading] = useState(false);
  const [createdBy, setCreatedBy] = useState(1);
  const [customerId, setCustomerId] = useState(""); // fallback only
  const token = localStorage.getItem("token");

  const tokenHeader = { Authorization: `Bearer ${token}` };

  const onPick = (e) => {
    const fs = Array.from(e.target.files || []).filter((f) =>
      f.name.toLowerCase().endsWith(".json")
    );
    setFiles(fs);
  };

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsText(file, "utf-8");
    });

  // ---------- Customer helpers ----------
  const normalizeName = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[\s\.,\-_/]+/g, " ") // collapse punctuation/whitespace
      .replace(/\s+/g, " ")
      .trim();

  const fetchCustomers = async (inputValue) => {
    const r = await axios.get(`${API_BASE_URL}/api/customers/rahultemp`, {
      params: { q: inputValue, limit: 20 },
      headers: tokenHeader,
    });
    const arr = Array.isArray(r.data) ? r.data : r.data?.rows || [];
    return arr.map((c) => ({ value: c.id, label: c.name }));
  };

  const autoResolveCustomer = async (name) => {
    if (!name) return null;
    try {
      const r = await axios.get(`${API_BASE_URL}/api/customers/rahultemp`, {
        params: { q: name, limit: 10 },
        headers: tokenHeader,
      });
      const rows = Array.isArray(r.data) ? r.data : r.data?.rows || [];
      if (!rows.length) return null;

      const target = normalizeName(name);
      const exact =
        rows.find((c) => normalizeName(c.name) === target) || null;

      const pick = exact || (rows.length === 1 ? rows[0] : null);
      return pick ? { value: pick.id, label: pick.name } : null;
    } catch {
      return null;
    }
  };

  // ---------- JSON helpers ----------
  // produce JSON text with quantity/free_qty split from scheme "a+b"
  const applySchemeSplitToJsonText = (jsonText) => {
    const data = JSON.parse(jsonText);
    if (Array.isArray(data.items)) {
      data.items = data.items.map((it) => {
        const qty = Number(it.quantity) || 0;
        const m = /^\s*(\d+)\s*\+\s*(\d+)\s*$/.exec(String(it.scheme || ""));
        if (m && qty === Number(m[1]) + Number(m[2])) {
          return { ...it, quantity: Number(m[1]), free_qty: Number(m[2]) };
        }
        return it;
      });
    }
    return JSON.stringify(data, null, 2);
  };

  const tryServerPreview = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API_BASE_URL}/api/cnUpload/preview`, fd, {
      headers: { ...tokenHeader, "Content-Type": "multipart/form-data" },
    });
    return res.data;
  };

  const localPreviewFromJson = (jsonText, fileName) => {
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      throw new Error("Invalid JSON");
    }

    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    const totals = parsed?.totals ?? {};
    const meta = parsed?.meta ?? {};

    const deduction = Number(totals.deduction) || 0;
    const otherAdj = Number(totals.other_adjustment) || 0;

    const sumTaxable = items.reduce((s, it) => {
      const rate = Number(it.rate) || 0;
      const qty = Number(it.quantity) || 0;
      const m = /^\s*(\d+)\s*\+\s*(\d+)\s*$/.exec(String(it.scheme || ""));
      const qtyPaid = m && qty === Number(m[1]) + Number(m[2]) ? Number(m[1]) : qty;
      const taxable = rate ? qtyPaid * rate : Number(it.amount) || 0;
      return s + taxable;
    }, 0);

    const cgst = Number(totals.cgst_total) || 0;
    const sgst = Number(totals.sgst_total) || 0;
    const gstTotal = Number(totals.gst_total) || cgst + sgst;

    const net =
      Number(totals.credit_note_total) ||
      (sumTaxable + gstTotal + deduction + otherAdj);

    return {
      header: {
        file: fileName,
        customer_name: meta.customer_name || null,
        credit_note_date: meta.credit_note_date || null,
      },
      items: items.map((it, idx) => ({
        idx: idx + 1,
        reason: it.reason,
        product_name: it.product_name,
        hsn: it.hsn,
        pack: it.pack,
        scheme: it.scheme || null,
        quantity: it.quantity,
        free_qty_calc: (() => {
          const m = /^\s*(\d+)\s*\+\s*(\d+)\s*$/.exec(String(it.scheme || ""));
          const qty = Number(it.quantity) || 0;
          return m && qty === Number(m[1]) + Number(m[2]) ? Number(m[2]) : 0;
        })(),
        batch_no: it.batch_no,
        expiry: it.expiry,
        rate: it.rate,
        amount: it.amount,
        ref_invoice_number: it.ref_invoice_number,
      })),
      totals: {
        computed: {
          taxable_sum: +sumTaxable.toFixed(2),
          gst_total: +gstTotal.toFixed(2),
          net_amount: +net.toFixed(2),
        },
        from_json: {
          line_total: totals.line_total ?? null,
          credit_note_total: totals.credit_note_total ?? null,
          gst_total: totals.gst_total ?? null,
          cgst_total: totals.cgst_total ?? null,
          sgst_total: totals.sgst_total ?? null,
          deduction: totals.deduction ?? null,
          other_adjustment: totals.other_adjustment ?? null,
        },
      },
      raw: parsed,
    };
  };

  // ---------- Preview flow ----------
  const handlePreview = async () => {
    if (!files.length) return alert("Pick one or more .json files");
    setLoading(true);
    const out = [];

    for (const f of files) {
      try {
        let preview;
        let local = false;

        // Try server preview first
        try {
          preview = await tryServerPreview(f);
        } catch {
          // Fallback: local preview
          const txt = await readFileAsText(f);
          preview = localPreviewFromJson(txt, f.name);
          local = true;
        }

        // Auto resolve customer from preview.header.customer_name
        let autoCust = null;
        try {
          autoCust = await autoResolveCustomer(preview?.header?.customer_name);
        } catch {
          autoCust = null;
        }

        out.push({
          file: f,
          name: f.name,
          preview,
          local,
          customer: autoCust,        // preselect if found
          customerAuto: !!autoCust,  // flag to show "Auto"
        });
      } catch (e) {
        out.push({
          file: f,
          name: f.name,
          error: e?.message || "Preview failed",
        });
      }
    }

    setRows(out);
    setLoading(false);
  };

  // ---------- Commit ----------
  const commitOne = async (row) => {
    if (!row?.file) return;
    try {
      const fd = new FormData();

      // send transformed JSON (quantity/free_qty split from scheme)
      const originalText = await readFileAsText(row.file);
      let originalParsed = null;

      try { 
        originalParsed = JSON.parse(originalText); 
      } catch { 
        originalParsed = null;
      }
      const creditNoteDateFromJson = originalParsed?.meta?.credit_note_date || null;

      const jsonForCommit = applySchemeSplitToJsonText(originalText);
      const blob = new Blob([jsonForCommit], { type: "application/json" });
      fd.append("file", blob, row.name);

      if (createdBy) fd.append("created_by", String(createdBy));
      const chosenCustomerId =
        row.customer?.value || (customerId ? Number(customerId) : null);
      if (chosenCustomerId) fd.append("customer_id", String(chosenCustomerId));

      if (creditNoteDateFromJson) fd.append("credit_note_date", creditNoteDateFromJson);

      await axios.post(`${API_BASE_URL}/api/cnUpload/commit`, fd, {
        headers: { ...tokenHeader, "Content-Type": "multipart/form-data" },
      });

      alert(`Committed: ${row.name}`);
      setRows((prev) => prev.filter((r) => r.name !== row.name));
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || "Commit failed");
    }
  };

  const commitAll = async () => {
    for (const r of rows) {
      // eslint-disable-next-line no-await-in-loop
      await commitOne(r);
    }
  };

  const hasRows = rows.length > 0;

  const num = (n) =>
    n == null
      ? "-"
      : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    } catch {
      return iso;
    }
  };

  useMemo(() => rows.filter((r) => !r.error).length, [rows]);

  return (
    <div className="p-6 max-w-7xl mx-auto bg-white shadow rounded">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-bold">Credit Notes — JSON Preview & Commit</h2>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">Created By</label>
          <input
            type="number"
            className="border px-3 py-2 rounded w-28"
            value={createdBy}
            onChange={(e) => setCreatedBy(parseInt(e.target.value || "0", 10))}
          />
          <label className="text-sm text-gray-600">Fallback Customer ID</label>
          <input
            type="number"
            className="border px-3 py-2 rounded w-32"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder="(optional)"
            title="Used only if a row has no selected customer"
          />
          <button
            onClick={commitAll}
            disabled={!hasRows || loading}
            className={`px-4 py-2 rounded ${
              hasRows ? "bg-green-600 text-white" : "bg-gray-300 text-gray-600"
            }`}
            title="Commit all listed files"
          >
            Commit All
          </button>
        </div>
      </div>

      <input type="file" accept=".json" multiple onChange={onPick} />
      <div className="flex gap-2 mt-4">
        <button
          onClick={handlePreview}
          disabled={!files.length || loading}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          {loading ? "Processing…" : `Preview ${files.length ? `(${files.length})` : ""}`}
        </button>
        <span className="text-sm text-gray-500">
          Sends to <code>/api/cnUpload/preview</code> if available; otherwise previews locally.
        </span>
      </div>

      {/* Results */}
      {!!rows.length && (
        <div className="mt-6 space-y-4">
          {rows.map((r, rIdx) => {
            if (r.error) {
              return (
                <div
                  key={`${r.name}-${rIdx}`}
                  className="border rounded p-4 bg-red-50 text-red-700"
                >
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-sm mt-1">Error: {r.error}</div>
                </div>
              );
            }

            const p = r.preview;
            const items = p?.items || [];
            const header = p?.header || {};
            const totals = p?.totals || {};
            const computed = totals?.computed || {};
            const fromJson = totals?.from_json || {};

            const parsedCustomer = header.customer_name || "";

            const matchedToParsed =
              !!parsedCustomer &&
              !!r.customer &&
              normalizeName(parsedCustomer) === normalizeName(r.customer.label);

            return (
              <div key={`${r.name}-${rIdx}`} className="border rounded-lg overflow-hidden">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between p-4 bg-gray-50">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-8">
                    <div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-gray-600">
                        {r.local ? "Local preview" : "Server preview"}
                      </div>
                    </div>

                    {/* Parsed customer chip */}
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs bg-white">
                        <FiUser />
                        <span className="text-gray-700">
                          {parsedCustomer ? parsedCustomer : "No customer in JSON"}
                        </span>
                      </span>
                      {parsedCustomer && r.customer ? (
                        matchedToParsed ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <FiCheckCircle /> Matched
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                            <FiXCircle /> Selected ≠ Parsed
                          </span>
                        )
                      ) : parsedCustomer ? (
                        <span className="inline-flex items-center gap-1 text-slate-500 text-xs">
                          Try selecting or search…
                        </span>
                      ) : null}
                    </div>

                    {/* Auto customer selector (per row) */}
                    <div className="min-w-[320px]">
                      <div className="text-xs text-gray-500 mb-1">Customer</div>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        value={r.customer || null}
                        loadOptions={fetchCustomers}
                        onChange={(opt) => {
                          setRows((prev) =>
                            prev.map((row, i) =>
                              i === rIdx ? { ...row, customer: opt, customerAuto: false } : row
                            )
                          );
                        }}
                        placeholder={parsedCustomer || "Search customer…"}
                        classNamePrefix="sel"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: 38,
                            borderRadius: 10,
                            boxShadow: "none",
                            borderColor: "#e5e7eb",
                          }),
                          menu: (base) => ({ ...base, zIndex: 50 }),
                        }}
                      />
                      {r.customerAuto && r.customer && (
                        <div className="text-[11px] text-emerald-700 mt-1">
                          Auto-selected: <span className="font-medium">{r.customer.label}</span>
                        </div>
                      )}
                    </div>

                    <div className="text-sm">
                      <div>
                        <span className="text-gray-500">Date:</span>{" "}
                        {formatDate(header.credit_note_date)}
                      </div>
                      <div>
                        <span className="text-gray-500">Items:</span> {items.length}
                      </div>
                    </div>

                    <div className="text-sm">
                      <div>
                        <span className="text-gray-500">Taxable (calc):</span>{" "}
                        {num(computed.taxable_sum)}
                      </div>
                      <div>
                        <span className="text-gray-500">GST (calc):</span>{" "}
                        {num(computed.gst_total)}
                      </div>
                      <div>
                        <span className="text-gray-500">Net (calc):</span>{" "}
                        {num(computed.net_amount)}
                      </div>
                    </div>

                    <div className="text-sm">
                      <div>
                        <span className="text-gray-500">CN Total (JSON):</span>{" "}
                        {num(fromJson.credit_note_total)}
                      </div>
                      <div>
                        <span className="text-gray-500">GST (JSON):</span>{" "}
                        {num(fromJson.gst_total)}
                      </div>
                      <div>
                        <span className="text-gray-500">Other Adj. (JSON):</span>{" "}
                        {num(fromJson.other_adjustment)}
                      </div>
                      <div>
                        <span className="text-gray-500">Deduction (JSON):</span>{" "}
                        {num(fromJson.deduction)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        window.confirm("Commit this credit note?") && commitOne(r)
                      }
                      className="px-4 py-2 rounded bg-green-600 text-white"
                    >
                      Commit
                    </button>
                  </div>
                </div>

                <div className="p-4 overflow-x-auto">
                  {!items.length ? (
                    <div className="text-sm text-gray-600">No items.</div>
                  ) : (
                    <table className="min-w-full text-sm border">
                      <thead className="bg-gray-100">
                        <tr>
                          {[
                            "reason",
                            "product_name",
                            "hsn",
                            "pack",
                            "scheme",
                            "quantity",
                            "free_qty",
                            "batch_no",
                            "expiry",
                            "rate",
                            "amount",
                            "ref_invoice_number",
                          ].map((k) => (
                            <th key={k} className="border px-2 py-1 text-left">
                              {k}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, idx) => (
                          <tr key={`${r.name}-row-${idx}`}>
                            <td className="border px-2 py-1">{it.reason ?? "-"}</td>
                            <td className="border px-2 py-1">{it.product_name ?? "-"}</td>
                            <td className="border px-2 py-1">{it.hsn ?? "-"}</td>
                            <td className="border px-2 py-1">{it.pack ?? "-"}</td>
                            <td className="border px-2 py-1">{it.scheme ?? "-"}</td>
                            <td className="border px-2 py-1 text-right">{num(it.quantity)}</td>
                            <td className="border px-2 py-1 text-right">{num(it.free_qty_calc)}</td>
                            <td
                              className={`border px-2 py-1 ${
                                !it.batch_no ? "bg-red-50 text-red-700" : ""
                              }`}
                            >
                              {it.batch_no ?? "-"}
                            </td>
                            <td className="border px-2 py-1">{it.expiry ?? "-"}</td>
                            <td className="border px-2 py-1 text-right">{num(it.rate)}</td>
                            <td className="border px-2 py-1 text-right">{num(it.amount)}</td>
                            <td
                              className={`border px-2 py-1 ${
                                !it.ref_invoice_number ? "bg-yellow-50 text-yellow-800" : ""
                              }`}
                            >
                              {it.ref_invoice_number ?? "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!rows.length && <div className="mt-6 text-sm text-gray-500"></div>}
    </div>
  );
}
